import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  Reorder as ReorderIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";
import adminModuleService from "../services/adminModuleService";
import StepTypeSelector from "./StepTypeSelector";

// Sortable Step Item Component
function SortableStepItem({
  step,
  index,
  stepType,
  onEdit,
  onDelete,
  loading,
  isReorderMode,
  theme,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: isReorderMode ? "none" : "auto",
  };

  const stepTypeLabel =
    stepType === "3d-model"
      ? "3D Model"
      : stepType === "video"
      ? "Video"
      : stepType === "image"
      ? "Image"
      : "Unknown";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disableGutters
      sx={{
        p: 0,
        mb: 1.5,
      }}
      {...(isReorderMode ? { ...attributes, ...listeners } : {})}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: 2,
          border: "2px solid",
          borderColor: isDragging ? theme.palette.primary.main : "divider",
          borderRadius: 2,
          bgcolor: isDragging
            ? alpha(theme.palette.primary.main, 0.05)
            : "background.paper",
          cursor: isReorderMode ? "grab" : "default",
          transition: "all 0.2s ease",
          position: "relative",
          "&:hover": isReorderMode
            ? {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 4px 12px ${alpha(
                  theme.palette.primary.main,
                  0.15
                )}`,
              }
            : {},
          ...(isReorderMode && {
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              borderRadius: "2px 0 0 2px",
            },
          }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {/* Drag Handle Icon for Reorder Mode */}
          {isReorderMode && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: 28, // Align with title
                color: "text.secondary",
                cursor: "grab",
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}

          {/* Main Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Header: Order + Title + Type */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 1,
                pr: isReorderMode ? 0 : 4,
              }}
            >
              <Chip
                label={`#${step.order || index + 1}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, height: 24, fontSize: "0.75rem" }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={700}
                noWrap
                sx={{ maxWidth: "100%" }}
              >
                {step.title || "Untitled Topic"}
              </Typography>
              <Chip
                label={stepTypeLabel}
                size="small"
                variant="outlined"
                color={
                  stepType === "3d-model"
                    ? "primary"
                    : stepType === "video"
                    ? "error"
                    : stepType === "image"
                    ? "success"
                    : "default"
                }
                sx={{
                  height: { xs: 22, sm: 24 },
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" }
                }}
              />
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                fontSize: "0.85rem",
              }}
            >
              {step.content || "No content"}
            </Typography>

            {/* Info Chips */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`${step.duration || 30}s`}
                size="small"
                variant="outlined"
                sx={{
                  height: { xs: 22, sm: 24 },
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                  bgcolor: alpha(theme.palette.action.hover, 0.05),
                }}
              />
              {stepType === "3d-model" && (
                <Chip
                  label={`${step.hotspots?.length || 0} hotspots`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: { xs: 22, sm: 24 },
                    fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                  }}
                />
              )}
              {stepType === "video" && (
                <Chip
                  label={`${step.videoAnnotations?.length || 0} annotations`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: { xs: 22, sm: 24 },
                    fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                  }}
                />
              )}
              {stepType === "image" && (
                <Chip
                  label={`${step.imageAnnotations?.length || 0} annotations`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: { xs: 22, sm: 24 },
                    fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                  }}
                />
              )}

              <Chip
                label={formatDate(step.updatedAt)}
                size="small"
                variant="outlined"
                sx={{
                  height: { xs: 22, sm: 24 },
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                  bgcolor: alpha(theme.palette.action.hover, 0.05),
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Actions - Absolute Positioned */}
        {!isReorderMode && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 0.5,
            }}
          >
            <IconButton
              size="small"
              onClick={() => onEdit(step)}
              disabled={loading}
              sx={{
                padding: 0.5,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(step)}
              disabled={loading}
              sx={{
                padding: 0.5,
                bgcolor: alpha(theme.palette.error.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.15) },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    </ListItem>
  );
}

function StepsManager({
  moduleId,
  moduleName,
  moduleData,
  onClose,
  onDataChanged,
}) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openStepTypeSelector, setOpenStepTypeSelector] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [originalSteps, setOriginalSteps] = useState([]);
  const [reorderLoading, setReorderLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load steps
  useEffect(() => {
    const loadSteps = async () => {
      try {
        setLoading(true);
        const stepsResponse = await adminModuleService.getSteps(moduleId);
        setSteps(stepsResponse || []);
      } catch (error) {
        console.error("Error loading topics:", error);
        setSnackbar({
          open: true,
          message: `Error loading topics: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (moduleId) {
      loadSteps();
    }
  }, [moduleId]);

  // Detect step type based on step data
  const detectStepType = (step) => {
    if (step.stepType) {
      return step.stepType;
    }

    // Fallback detection based on content
    if (step.videoUrl || step.videoFile) {
      return "video";
    } else if (step.imageUrl || step.imageFile) {
      return "image";
    } else if (step.hotspots || step.camera) {
      return "3d-model";
    }

    // Default to 3d-model for backward compatibility
    return "3d-model";
  };

  // Navigate to appropriate editor based on step type
  const navigateToEditor = (stepType, stepData = null) => {
    // Ensure we have full module data
    const fullModuleData = moduleData || { id: moduleId, title: moduleName };

    const navigationState = {
      moduleId: moduleId,
      moduleData: fullModuleData,
    };

    if (stepData) {
      // Edit existing step - use edit files
      navigationState.stepId = stepData.id;
      navigationState.stepData = stepData;

      // console.log("🚀 Navigating to edit topic with full data:", {
      //   stepType,
      //   moduleData: fullModuleData,
      //   stepData: stepData,
      // });

      switch (stepType) {
        case "3d-model":
          navigate(`/module/${moduleId}/edit-step-editor/${stepData.id}`, { state: navigationState });
          break;
        case "video":
          navigate(`/module/${moduleId}/edit-video-step/${stepData.id}`, { state: navigationState });
          break;
        case "image":
          navigate(`/module/${moduleId}/edit-image-step/${stepData.id}`, { state: navigationState });
          break;
        default:
          console.warn("Unknown step type for editing:", stepType);
      }
    } else {
      // Create new step - use create files
      // console.log("🚀 Navigating to create step with full data:", {
      //   stepType,
      //   moduleData: fullModuleData,
      // });

      switch (stepType) {
        case "3d-model":
          navigate(`/module/step-editor/${moduleId}`, { state: navigationState });
          break;
        case "video":
          navigate(`/module/video-step-editor/${moduleId}`, { state: navigationState });
          break;
        case "image":
          navigate(`/module/image-step-editor/${moduleId}`, { state: navigationState });
          break;
        default:
          console.warn("Unknown topic type for creation:", stepType);
      }
    }
  };

  const handleCreateStep = () => {
    setOpenStepTypeSelector(true);
  };

  const handleSelectStepType = (stepType) => {
    setOpenStepTypeSelector(false);
    navigateToEditor(stepType);

    setSnackbar({
      open: true,
      message: `Opening ${stepType} topic editor...`,
      severity: "info",
    });
  };

  const handleEditStep = (step) => {
    const stepType = detectStepType(step);
    navigateToEditor(stepType, step);

    setSnackbar({
      open: true,
      message: `Opening ${stepType} topic editor for editing...`,
      severity: "info",
    });
  };

  const handleDeleteStep = async () => {
    if (!stepToDelete) return;

    try {
      setLoading(true);
      await adminModuleService.deleteStep(moduleId, stepToDelete.id);

      // Remove from local state
      setSteps(steps.filter((s) => s.id !== stepToDelete.id));

      setSnackbar({
        open: true,
        message: "Topic deleted successfully!",
        severity: "info",
      });

      setOpenDeleteDialog(false);
      setStepToDelete(null);

      // Notify parent that data has changed
      if (onDataChanged) {
        onDataChanged();
      }
    } catch (error) {
      console.error("Error deleting topic:", error);
      setSnackbar({
        open: true,
        message: `Error deleting topic: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Update order property for each step
        return newOrder.map((step, index) => ({
          ...step,
          order: index + 1,
        }));
      });
    }
  };

  // Toggle reorder mode
  const handleToggleReorderMode = () => {
    if (!isReorderMode) {
      // Entering reorder mode - save original order
      setOriginalSteps([...steps]);
      setIsReorderMode(true);
      setSnackbar({
        open: true,
        message: "Reorder mode enabled. Drag steps to change their order.",
        severity: "info",
      });
    } else {
      // Exiting reorder mode - restore original order
      setSteps(originalSteps);
      setIsReorderMode(false);
      setSnackbar({
        open: true,
        message: "Reorder mode cancelled. Order restored.",
        severity: "info",
      });
    }
  };

  // Save new order
  const handleSaveOrder = async () => {
    try {
      // console.log("🔄 Starting handleSaveOrder...");
      // console.log(
      //   "📋 Current topic:",
      //   steps.map((s) => ({ id: s.id, title: s.title, order: s.order }))
      // );

      setReorderLoading(true);

      // Update order for each step - always call API to ensure sync
      const updatePromises = steps.map((step, index) => {
        const newOrder = index + 1;
        // console.log(
        //   `🔄 Processing topic ${step.id}: current order ${step.order} -> new order ${newOrder}`
        // );

        // Always update to ensure order is synchronized
        return adminModuleService.updateStepOrder(moduleId, step.id, newOrder);
      });

      // console.log("🚀 Calling API for all steps...");
      await Promise.all(updatePromises);

      // console.log("✅ All API calls completed successfully");
      setIsReorderMode(false);
      setSnackbar({
        open: true,
        message: "Topic order updated successfully!",
        severity: "success",
      });

      // Notify parent that data has changed
      if (onDataChanged) {
        onDataChanged();
      }
    } catch (error) {
      console.error("❌ Error updating topic order:", error);
      setSnackbar({
        open: true,
        message: `Error updating order: ${error.message}`,
        severity: "error",
      });
      // Restore original order on error
      setSteps(originalSteps);
    } finally {
      setReorderLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ height: "80vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      {/* Header */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          p: { xs: 2, sm: 3 },
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ pr: 4 }}>
          {" "}
          {/* Add padding right to avoid overlap with close button */}
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
          >
            Topics Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Module: {moduleName}
          </Typography>
        </Box>

        {/* Close Button - Absolute Positioned */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: alpha(theme.palette.grey[500], 0.1),
            "&:hover": {
              bgcolor: alpha(theme.palette.grey[500], 0.2),
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            mr: 2,
            mt: { xs: 2, sm: 2 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {!isReorderMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<ReorderIcon />}
                onClick={handleToggleReorderMode}
                disabled={loading || steps.length < 2}
                size="medium"
                fullWidth
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Reorder Topics
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleToggleReorderMode}
                disabled={reorderLoading}
                size="medium"
                fullWidth
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={
                  reorderLoading ? <CircularProgress size={16} /> : <SaveIcon />
                }
                onClick={handleSaveOrder}
                disabled={reorderLoading}
                color="success"
                size="medium"
                fullWidth
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {reorderLoading ? "Saving..." : "Save Order"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={48} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Total Topics: {steps.length}
              </Typography>
              {isReorderMode && (
                <Chip
                  label="Reorder Mode Publish"
                  color="primary"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            {steps.length === 0 ? (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: "2px dashed",
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  borderRadius: 3,
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="h6">
                  No topics created yet
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Create your first topic to get started with this module
                </Typography>
                {/* <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateStep}
                  size="large"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Create First Topic
                </Button> */}
              </Paper>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={steps.map((step) => step.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <List disablePadding>
                    {steps.map((step, index) => {
                      const stepType = detectStepType(step);

                      return (
                        <SortableStepItem
                          key={step.id}
                          step={step}
                          index={index}
                          stepType={stepType}
                          onEdit={handleEditStep}
                          onDelete={(step) => {
                            setStepToDelete(step);
                            setOpenDeleteDialog(true);
                          }}
                          loading={loading}
                          isReorderMode={isReorderMode}
                          theme={theme}
                        />
                      );
                    })}
                  </List>
                </SortableContext>
              </DndContext>
            )}
          </>
        )}
      </Box>

      {/* Topic Type Selector Dialog */}
      <StepTypeSelector
        open={openStepTypeSelector}
        onClose={() => setOpenStepTypeSelector(false)}
        onSelectType={handleSelectStepType}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="delete-step-dialog"
      >
        <DialogTitle>Confirm Topic Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete the topic "{stepToDelete?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteStep}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete Topic
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default StepsManager;
